import styled from '@/styles';
import type { WorkerCurrentStatus } from '@/services/workersV2API';
import { statusPillLabel, statusSubtext } from '../mappings';

const Pill = styled('span', {
  display: 'inline-block',
  padding: '4px 10px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  variants: {
    tone: {
      amber: { backgroundColor: '#FFF4D8', color: '#BB8700' },
      red: { backgroundColor: '#FFDFD8', color: '#CF040C' },
      green: { backgroundColor: '#E7F4E6', color: '#1E8E3E' },
      gray: { backgroundColor: '#EDEDED', color: '#5A5A5A' },
    },
  },
});

const Sub = styled('div', {
  marginTop: '4px',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  variants: {
    stalled: {
      true: { color: '#CF040C' },
      false: { color: '#7A7A7A' },
    },
  },
});

const toneFor = (
  s: WorkerCurrentStatus,
): 'amber' | 'red' | 'green' | 'gray' => {
  if (s.state === 'enabled') return 'green';
  if (s.state === 'disabled') return 'gray';
  return s.stalled ? 'red' : 'amber';
};

const WarningIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    data-testid="statusWarningIcon"
  >
    <path
      d="M10 3l8 14H2L10 3z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M10 8v4M10 14.5v.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const CurrentStatusPill = ({ status }: { status: WorkerCurrentStatus }) => {
  const sub = statusSubtext(status);
  return (
    <div>
      <Pill tone={toneFor(status)} data-testid="statusPill">
        {statusPillLabel(status)}
      </Pill>
      {sub && (
        <Sub
          stalled={status.stalled}
          data-testid="statusElapsed"
          data-stalled={status.stalled ? 'true' : 'false'}
        >
          {status.stalled && <WarningIcon />}
          {sub}
        </Sub>
      )}
    </div>
  );
};

export default CurrentStatusPill;
